import React from "react";
import SwipeableNotif from "./SwipeableNotif";
import { CustomIcon } from "./shared/CustomIcon";
import {
  NOTIF_SECTIONS,
  NOTIF_OTHER_SECTION,
  notifSectionFor,
} from "../lib/notifSections";

// =============================================================
// NotifPanel — bell-icon dropdown panel rendered by Topbar. Owns
// section grouping, override request inline actions, swipe-to-delete,
// undo toast, and the "View all notifications" footer link. All state
// is driven by props from KingdomConnect.jsx so the panel stays a
// pure presentation component.
// =============================================================
export default function NotifPanel({
  notifications,
  currentUser,
  clearAllNotifications,
  collapsedNotifSections,
  setCollapsedNotifSections,
  handleNotifClick,
  decideOverrideRequest,
  queueDeletion,
  pendingDelete,
  undoDelete,
  onClose,
  navigate,
}) {
  return (
    <div className="notif-panel" onClick={e => e.stopPropagation()} data-testid="notif-panel">
      <div className="notif-panel-header">
        <span className="title">Notifications</span>
        <button
          className="clear-all"
          data-testid="notif-clear-all"
          disabled={notifications.length === 0}
          onClick={clearAllNotifications}
        >Clear All</button>
      </div>
      <div className="notif-panel-list" data-testid="notif-panel-list">
        {(() => {
          // Hide archived (>30 days) from the bell.
          const THIRTY_D_AGO = Date.now() - 30 * 86400 * 1000;
          const isFresh = (n) => {
            const t = Date.parse(n.createdAt || n.time || 0);
            return !Number.isFinite(t) || t >= THIRTY_D_AGO;
          };
          const visible = notifications.filter(isFresh);
          const grouped = {};
          for (const n of visible) {
            const sid = notifSectionFor(n.type);
            (grouped[sid] = grouped[sid] || []).push(n);
          }
          const sectionsToRender = [...NOTIF_SECTIONS, NOTIF_OTHER_SECTION].filter(s => (grouped[s.id] || []).length > 0);
          if (sectionsToRender.length === 0) {
            return <div className="notif-empty" data-testid="notif-empty">All caught up — no notifications.</div>;
          }
          return sectionsToRender.map(sec => {
            const list = grouped[sec.id] || [];
            const unread = list.filter(n => Array.isArray(n.read) ? !n.read.includes(currentUser.id) : !n.read).length;
            const collapsed = collapsedNotifSections[sec.id];
            return (
              <div key={sec.id} data-testid={`notif-section-${sec.id}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                <button
                  data-testid={`notif-section-toggle-${sec.id}`}
                  onClick={() => setCollapsedNotifSections(prev => ({ ...prev, [sec.id]: !prev[sec.id] }))}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 14px", background: "color-mix(in srgb, var(--kc-accent) 4%, transparent)", border: "none",
                    color: "var(--kc-text)", cursor: "pointer", textAlign: "left",
                    fontFamily: "'Cinzel',serif", fontSize: 12, fontWeight: 700,
                    letterSpacing: "0.06em", textTransform: "uppercase",
                  }}
                >
                  <span style={{ fontSize: 16, display: "inline-flex", alignItems: "center" }}>
                    {sec.customIcon ? <CustomIcon name={sec.customIcon} size={18} /> : sec.icon}
                  </span>
                  <span style={{ flex: 1 }}>{sec.label}</span>
                  {unread > 0 && (
                    <span
                      data-testid={`notif-section-count-${sec.id}`}
                      style={{
                        minWidth: 22, padding: "0 7px", height: 18, lineHeight: "18px",
                        background: "var(--kc-accent)", color: "#1A1A1A", borderRadius: 999,
                        fontSize: 10, fontWeight: 800, textAlign: "center",
                        fontFamily: "'Montserrat',sans-serif",
                      }}
                    >{unread}</span>
                  )}
                  <span style={{ fontSize: 11, color: "var(--kc-sub)" }}>{collapsed ? "▾" : "▴"}</span>
                </button>
                {!collapsed && list.map(n => {
                  const isRead = Array.isArray(n.read) ? n.read.includes(currentUser.id) : !!n.read;
                  if (n.type === "override_request") {
                    return (
                      <div
                        key={n.id}
                        data-testid={`notif-item-${n.id}`}
                        className="notif-item"
                        style={{ background: isRead ? "" : "color-mix(in srgb, var(--kc-accent) 4%, transparent)", display: "flex", flexDirection: "column", gap: 8, padding: "10px 14px" }}
                      >
                        <div className="notif-title" onClick={() => handleNotifClick(n)} role="link" tabIndex={0} style={{ cursor: "pointer" }}>
                          {!isRead && <span style={{ display: "inline-block", width: 6, height: 6, background: "var(--kc-accent)", borderRadius: "50%", marginRight: 6, verticalAlign: "middle" }} />}
                          {n.text}
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            className="btn btn-sm"
                            data-testid={`notif-override-approve-${n.id}`}
                            onClick={(e) => { e.stopPropagation(); decideOverrideRequest(n, "approved"); }}
                            style={{ flex: 1, background: "rgba(74,222,128,0.18)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.50)", fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4 }}
                          ><CustomIcon name="check" size={12} /> Approve</button>
                          <button
                            className="btn btn-sm"
                            data-testid={`notif-override-deny-${n.id}`}
                            onClick={(e) => { e.stopPropagation(); decideOverrideRequest(n, "denied"); }}
                            style={{ flex: 1, background: "rgba(255,107,107,0.18)", color: "#ff6b6b", border: "1px solid rgba(255,107,107,0.50)", fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4 }}
                          ><CustomIcon name="close" size={12} /> Deny</button>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <SwipeableNotif
                      key={n.id}
                      notif={n}
                      isRead={isRead}
                      data-testid={`notif-item-${n.id}`}
                      onClick={handleNotifClick}
                      onDelete={(notif) => queueDeletion([notif])}
                    />
                  );
                })}
              </div>
            );
          });
        })()}
      </div>
      {pendingDelete && (
        <div className="undo-toast" data-testid="notif-undo-toast">
          <span className="undo-text">
            {pendingDelete.items.length === 1
              ? "Notification deleted"
              : `${pendingDelete.items.length} notifications cleared`}
          </span>
          <button className="undo-btn" data-testid="notif-undo-btn" onClick={undoDelete}>Undo</button>
        </div>
      )}
      {pendingDelete && <div className="undo-progress" key={pendingDelete.timer} />}
      <div
        data-testid="notif-view-all-link"
        role="button"
        tabIndex={0}
        onClick={() => { onClose && onClose(); navigate("/notifications"); }}
        style={{
          padding: "10px 14px", textAlign: "center", cursor: "pointer",
          borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)",
          background: "color-mix(in srgb, var(--kc-accent) 6%, transparent)",
          fontFamily: "'Cinzel',serif", fontSize: 11, fontWeight: 700,
          letterSpacing: "0.10em", textTransform: "uppercase",
          color: "var(--kc-accent)",
        }}
      >View all notifications →</div>
    </div>
  );
}
